import { DEFAULT_LANGUAGE } from '@/constants';
import { LanguageCode } from '@/interfaces';
import type { IMainTranslation, INotificationTranslation, ITrackingTranslation } from '@/modules/pmt/interfaces';
import { getMasterTranslationLanguageCode, languageFieldsMapping, translationDataModify } from '@/utils';
import callContentful from './base.fetchers';

type GetTranslationDataArgs = (args: {
  isPreviewMode: boolean;
  languageCode: LanguageCode;
}) => Promise<{
  mainTranslation: IMainTranslation;
  trackingTranslations: ITrackingTranslation[];
  notificationTranslations: INotificationTranslation[];
}>;

export const getTranslationData: GetTranslationDataArgs = async ({
  isPreviewMode,
  languageCode = LanguageCode.English,
}) => {
  const mappedLanguageFields = languageFieldsMapping(languageCode);
  const languageField = getMasterTranslationLanguageCode(languageCode);

  const query = `
    {
      defaultMainTranslationCollection: mainTranslationCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        limit: 1
        order: sys_firstPublishedAt_DESC
      ) {
        items {
          sys {
            id
          }
          parcelListSectionTitleMobile {
            en
          }
          parcelListSectionTitleDesktop {
            en
          }
          appBadgeSectionHeader {
            en
          }
          appBadgeMainText {
            en
          }
          appBadgeSubText {
            en
          }
        }
      }
      mainTranslationCollection(
        preview: ${isPreviewMode}
        locale: "${mappedLanguageFields.locale}"
        limit: 1
        order: sys_firstPublishedAt_DESC
      ) {
        items {
          sys {
            id
          }
          parcelListSectionTitleMobile {
            ${languageField}
          }
          parcelListSectionTitleDesktop {
            ${languageField}
          }
          appBadgeSectionHeader {
            ${languageField}
          }
          appBadgeMainText {
            ${languageField}
          }
          appBadgeSubText {
            ${languageField}
          }
          appBadgeImage {
            contentType(locale: "${DEFAULT_LANGUAGE}")
            url(transform: {width: 400}, locale: "${DEFAULT_LANGUAGE}")
          }
          buttonLinkIos
          buttonLinkAndroid
          country
        }
      }
      defaultTrackingTranslationCollection: trackingTranslationCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        limit: 1
        order: sys_firstPublishedAt_DESC
      ) {
        items {
          sys {
            id
          }
          identifier
          mainTextMobile {
            en
          }
          subTextMobile {
            en
          }
          sectionTitleDesktop {
            en
          }
          mainTextDesktop {
            en
          }
          subTextDesktop {
            en
          }
          mainTextGrantAccessPopup {
            en
          }
          subTextGrantAccessPopup {
            en
          }
          mainButtonGrantAccessPopup {
            en
          }
        }
      }
      trackingTranslationCollection(
        preview: ${isPreviewMode}
        locale: "${mappedLanguageFields.locale}"
        limit: 10
        order: sys_firstPublishedAt_DESC
      ) {
        items {
          sys {
            id
          }
          identifier
          mainTextMobile {
            ${languageField}
          }
          subTextMobile {
            ${languageField}
          }
          sectionTitleDesktop {
            ${languageField}
          }
          mainTextDesktop {
            ${languageField}
          }
          subTextDesktop {
            ${languageField}
          }
          mainTextGrantAccessPopup {
            ${languageField}
          }
          subTextGrantAccessPopup {
            ${languageField}
          }
          mainButtonGrantAccessPopup {
            ${languageField}
          }
        }
      }
      defaultNotificationTranslationCollection: notificationTranslationCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        limit: 1
        order: sys_firstPublishedAt_DESC
      ) {
        items {
          sys {
            id
          }
          identifier
          mobileText {
            en
          }
          desktopText {
            en
          }
          buttonText {
            en
          }
          emailSyncDesktopMainText {
            en
          }
          emailSyncMobileMainText {
            en
          }
          emailSyncButtonText {
            en
          }
          loggedInEsMainText {
            en
          }
          loggedInEsHasParcelsButtonText {
            en
          }
          loggedInEsNoParcelsButtonText {
            en
          }
        }
      }
      notificationTranslationCollection(
        preview: ${isPreviewMode}
        locale: "${mappedLanguageFields.locale}"
        limit: 10
        order: sys_firstPublishedAt_DESC
      ) {
        items {
          sys {
            id
          }
          identifier
          mobileText {
            ${languageField}
          }
          desktopText {
            ${languageField}
          }
          buttonText {
            ${languageField}
          }
          emailSyncDesktopMainText {
            ${languageField}
          }
          emailSyncMobileMainText {
            ${languageField}
          }
          emailSyncButtonText {
            ${languageField}
          }
          loggedInEsMainText {
            ${languageField}
          }
          loggedInEsHasParcelsButtonText {
            ${languageField}
          }
          loggedInEsNoParcelsButtonText {
            ${languageField}
          }
        }
      }
    }
  `;

  const slug = `/${languageCode === LanguageCode.English ? '' : '/'}track-my-parcel`;
  const response = await callContentful(query, isPreviewMode, slug, 'translation.fetchers.getTranslationData');
  const rawDefaultMainData: IMainTranslation = response.data?.defaultMainTranslationCollection?.items?.[0];
  const rawDefaultTrackingData: ITrackingTranslation = response.data?.defaultTrackingTranslationCollection?.items?.[0];
  const rawDefaultNotificationData: INotificationTranslation =
    response.data?.defaultNotificationTranslationCollection?.items?.[0];

  const rawMainData: IMainTranslation = response.data?.mainTranslationCollection?.items?.[0];
  const rawTrackingData: ITrackingTranslation[] = response.data?.trackingTranslationCollection?.items;
  const rawNotificationData: INotificationTranslation[] = response.data?.notificationTranslationCollection?.items;

  // Transform multiLanguageField data for mainTranslation
  const transformMainTranslationData = (item: Record<string, unknown>) => {
    const parcelListSectionTitleMobile = item.parcelListSectionTitleMobile as Record<string, string> | null;
    const parcelListSectionTitleDesktop = item.parcelListSectionTitleDesktop as Record<string, string> | null;
    const appBadgeSectionHeader = item.appBadgeSectionHeader as Record<string, string> | null;
    const appBadgeMainText = item.appBadgeMainText as Record<string, string> | null;
    const appBadgeSubText = item.appBadgeSubText as Record<string, string> | null;

    return {
      ...item,
      parcelListSectionTitleMobile: parcelListSectionTitleMobile?.[languageField] || '',
      parcelListSectionTitleDesktop: parcelListSectionTitleDesktop?.[languageField] || '',
      appBadgeSectionHeader: appBadgeSectionHeader?.[languageField] || '',
      appBadgeMainText: appBadgeMainText?.[languageField] || '',
      appBadgeSubText: appBadgeSubText?.[languageField] || '',
    };
  };

  const transformDefaultMainTranslationData = (item: Record<string, unknown>) => {
    const parcelListSectionTitleMobile = item.parcelListSectionTitleMobile as Record<string, string> | null;
    const parcelListSectionTitleDesktop = item.parcelListSectionTitleDesktop as Record<string, string> | null;
    const appBadgeSectionHeader = item.appBadgeSectionHeader as Record<string, string> | null;
    const appBadgeMainText = item.appBadgeMainText as Record<string, string> | null;
    const appBadgeSubText = item.appBadgeSubText as Record<string, string> | null;

    return {
      ...item,
      parcelListSectionTitleMobile: parcelListSectionTitleMobile?.en || '',
      parcelListSectionTitleDesktop: parcelListSectionTitleDesktop?.en || '',
      appBadgeSectionHeader: appBadgeSectionHeader?.en || '',
      appBadgeMainText: appBadgeMainText?.en || '',
      appBadgeSubText: appBadgeSubText?.en || '',
    };
  };

  // Transform multiLanguageField data for trackingTranslation
  const transformTrackingTranslationData = (item: Record<string, unknown>) => {
    const sectionTitleDesktop = item.sectionTitleDesktop as Record<string, string> | null;
    const mainTextMobile = item.mainTextMobile as Record<string, string> | null;
    const mainTextDesktop = item.mainTextDesktop as Record<string, string> | null;
    const subTextMobile = item.subTextMobile as Record<string, string> | null;
    const subTextDesktop = item.subTextDesktop as Record<string, string> | null;
    const mainTextGrantAccessPopup = item.mainTextGrantAccessPopup as Record<string, string> | null;
    const subTextGrantAccessPopup = item.subTextGrantAccessPopup as Record<string, string> | null;
    const mainButtonGrantAccessPopup = item.mainButtonGrantAccessPopup as Record<string, string> | null;

    return {
      ...item,
      sectionTitleDesktop: sectionTitleDesktop?.[languageField] || '',
      mainTextMobile: mainTextMobile?.[languageField] || '',
      mainTextDesktop: mainTextDesktop?.[languageField] || '',
      subTextMobile: subTextMobile?.[languageField] || '',
      subTextDesktop: subTextDesktop?.[languageField] || '',
      mainTextGrantAccessPopup: mainTextGrantAccessPopup?.[languageField] || '',
      subTextGrantAccessPopup: subTextGrantAccessPopup?.[languageField] || '',
      mainButtonGrantAccessPopup: mainButtonGrantAccessPopup?.[languageField] || '',
    };
  };

  const transformDefaultTrackingTranslationData = (item: Record<string, unknown>) => {
    const sectionTitleDesktop = item.sectionTitleDesktop as Record<string, string> | null;
    const mainTextMobile = item.mainTextMobile as Record<string, string> | null;
    const mainTextDesktop = item.mainTextDesktop as Record<string, string> | null;
    const subTextMobile = item.subTextMobile as Record<string, string> | null;
    const subTextDesktop = item.subTextDesktop as Record<string, string> | null;
    const mainTextGrantAccessPopup = item.mainTextGrantAccessPopup as Record<string, string> | null;
    const subTextGrantAccessPopup = item.subTextGrantAccessPopup as Record<string, string> | null;
    const mainButtonGrantAccessPopup = item.mainButtonGrantAccessPopup as Record<string, string> | null;

    return {
      ...item,
      sectionTitleDesktop: sectionTitleDesktop?.en || '',
      mainTextMobile: mainTextMobile?.en || '',
      mainTextDesktop: mainTextDesktop?.en || '',
      subTextMobile: subTextMobile?.en || '',
      subTextDesktop: subTextDesktop?.en || '',
      mainTextGrantAccessPopup: mainTextGrantAccessPopup?.en || '',
      subTextGrantAccessPopup: subTextGrantAccessPopup?.en || '',
      mainButtonGrantAccessPopup: mainButtonGrantAccessPopup?.en || '',
    };
  };

  // Transform multiLanguageField data for notificationTranslation
  const transformNotificationTranslationData = (item: Record<string, unknown>) => {
    const mobileText = item.mobileText as Record<string, string> | null;
    const desktopText = item.desktopText as Record<string, string> | null;
    const buttonText = item.buttonText as Record<string, string> | null;
    const emailSyncDesktopMainText = item.emailSyncDesktopMainText as Record<string, string> | null;
    const emailSyncMobileMainText = item.emailSyncMobileMainText as Record<string, string> | null;
    const emailSyncButtonText = item.emailSyncButtonText as Record<string, string> | null;
    const loggedInEsMainText = item.loggedInEsMainText as Record<string, string> | null;
    const loggedInEsHasParcelsButtonText = item.loggedInEsHasParcelsButtonText as Record<string, string> | null;
    const loggedInEsNoParcelsButtonText = item.loggedInEsNoParcelsButtonText as Record<string, string> | null;

    return {
      ...item,
      mobileText: mobileText?.[languageField] || '',
      desktopText: desktopText?.[languageField] || '',
      buttonText: buttonText?.[languageField] || '',
      emailSyncDesktopMainText: emailSyncDesktopMainText?.[languageField] || '',
      emailSyncMobileMainText: emailSyncMobileMainText?.[languageField] || '',
      emailSyncButtonText: emailSyncButtonText?.[languageField] || '',
      loggedInEsMainText: loggedInEsMainText?.[languageField] || '',
      loggedInEsHasParcelsButtonText: loggedInEsHasParcelsButtonText?.[languageField] || '',
      loggedInEsNoParcelsButtonText: loggedInEsNoParcelsButtonText?.[languageField] || '',
    };
  };

  const transformDefaultNotificationTranslationData = (item: Record<string, unknown>) => {
    const mobileText = item.mobileText as Record<string, string> | null;
    const desktopText = item.desktopText as Record<string, string> | null;
    const buttonText = item.buttonText as Record<string, string> | null;
    const emailSyncDesktopMainText = item.emailSyncDesktopMainText as Record<string, string> | null;
    const emailSyncMobileMainText = item.emailSyncMobileMainText as Record<string, string> | null;
    const emailSyncButtonText = item.emailSyncButtonText as Record<string, string> | null;
    const loggedInEsMainText = item.loggedInEsMainText as Record<string, string> | null;
    const loggedInEsHasParcelsButtonText = item.loggedInEsHasParcelsButtonText as Record<string, string> | null;
    const loggedInEsNoParcelsButtonText = item.loggedInEsNoParcelsButtonText as Record<string, string> | null;

    return {
      ...item,
      mobileText: mobileText?.en || '',
      desktopText: desktopText?.en || '',
      buttonText: buttonText?.en || '',
      emailSyncDesktopMainText: emailSyncDesktopMainText?.en || '',
      emailSyncMobileMainText: emailSyncMobileMainText?.en || '',
      emailSyncButtonText: emailSyncButtonText?.en || '',
      loggedInEsMainText: loggedInEsMainText?.en || '',
      loggedInEsHasParcelsButtonText: loggedInEsHasParcelsButtonText?.en || '',
      loggedInEsNoParcelsButtonText: loggedInEsNoParcelsButtonText?.en || '',
    };
  };

  // Apply transformations
  const defaultMainData = rawDefaultMainData
    ? transformDefaultMainTranslationData(rawDefaultMainData as Record<string, unknown>)
    : {};
  const mainData = rawMainData ? transformMainTranslationData(rawMainData as Record<string, unknown>) : {};

  const defaultTrackingData = rawDefaultTrackingData
    ? transformDefaultTrackingTranslationData(rawDefaultTrackingData as Record<string, unknown>)
    : {};
  const trackingData = rawTrackingData?.map((item) =>
    transformTrackingTranslationData(item as Record<string, unknown>),
  );

  const defaultNotificationData = rawDefaultNotificationData
    ? transformDefaultNotificationTranslationData(rawDefaultNotificationData as Record<string, unknown>)
    : {};
  const notificationData = rawNotificationData?.map((item) =>
    transformNotificationTranslationData(item as Record<string, unknown>),
  );

  return {
    mainTranslation: translationDataModify(defaultMainData, mainData),
    trackingTranslations: trackingData?.map((trackingTranslation) =>
      translationDataModify(defaultTrackingData, trackingTranslation),
    ),
    notificationTranslations: notificationData?.map((notificationTranslation) =>
      translationDataModify(defaultNotificationData, notificationTranslation),
    ),
  };
};
